import { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Anchor,
  Badge,
  Divider,
  Loader,
  MultiSelect,
  Select,
  Stack,
  Switch,
  Tabs,
  Text,
  TextInput,
} from '@mantine/core'
import { modals } from '@mantine/modals'
import { IconAlertCircle, IconArrowLeft } from '@tabler/icons-react'
import type { MedplumClient } from '@medplum/core'
import { useMedplum } from '@medplum/react'
import { useQuery } from '@tanstack/react-query'
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom'
import {
  Controller,
  useForm,
  useWatch,
  type FieldValues,
  type Resolver,
  type UseFormReturn,
} from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  bridgeProviderIdFieldSchema,
  caqhProfileSchema,
  collectCaqhIssues,
  collectCredentialIssues,
  collectLiabilityIssues,
  collectPayerEnrollmentIssues,
  doseSpotClinicianIdFieldSchema,
  languagesSchema,
  liabilityCoverageSchema,
  licenseRowSchema,
  npiNumberSchema,
  payerEnrollmentRowInputSchema,
  LICENSES_NEED_PROFESSIONAL_ROLE_MESSAGE,
  PRACTITIONER_LANGUAGES,
  practitionerPhotoSchema,
  professionalRoleSchema,
  specialtySchema,
  stediProviderIdFieldSchema,
  type CaqhProfile,
  type LiabilityCoverage,
  type LicenseRow,
  type PayerEnrollmentRowInput,
  type PractitionerPhoto,
} from '@behuman/core-contract'
import { ACCENT } from '@/components/ui/primaryButton.tokens'
import { DirtyDot, SaveFooter } from '@/components/ui/SaveFooter'
import { usePractitioner, type PractitionerDetail } from '@/hooks/usePractitioner'
import { usePractitionerOpsScope } from '@/hooks/usePractitionerOpsScope'
import { useOrpc } from '@/hooks/useClient'
import { useMiddlewareClient } from '@/hooks/useMiddlewareClient'
import { useSetPractitionerBridgeProvider } from '@/hooks/useSetPractitionerBridgeProvider'
import { useSetPractitionerDoseSpotClinician } from '@/hooks/useSetPractitionerDoseSpotClinician'
import { useSetPractitionerStediProvider } from '@/hooks/useSetPractitionerStediProvider'
import { useWarnBeforeUnload } from '@/hooks/useWarnBeforeUnload'
import { useUpdatePractitionerCredentials } from '@/hooks/useUpdatePractitionerCredentials'
import { LicensesField } from '@/pages/Super/components/LicensesField'
import { uploadPendingFile } from '@/pages/Super/components/LicensesField.utils'
import { getInsurancePayersCatalog } from '@/pages/Settings/insurancePlans.service'
import { ProfessionalRoleFields } from '@/pages/Super/components/ProfessionalRoleFields'
import { SNOMED_USER_ROLES } from '@/utils'
import { CaqhProfileField } from './CaqhProfileField'
import { LiabilityCoverageField } from './LiabilityCoverageField'
import { PayerEnrollmentsField, type PayerEnrollmentsFormValues } from './PayerEnrollmentsField'
import { toPayerOptions } from './PayerEnrollmentsField.utils'
import { PhotoField } from './PhotoField'
import { ORGANIZATION_QUERY_PARAM } from './ProviderList'

// The active tab lives in the URL so a reload and a shared link land on the same tab.
const TAB_QUERY_PARAM = 'tab'
const PROVIDER_TABS = ['profile', 'licenses', 'caqh', 'payer-enrollments', 'integrations'] as const
type ProviderTab = (typeof PROVIDER_TABS)[number]
const DEFAULT_TAB: ProviderTab = 'profile'

function parseTab(value: string | null): ProviderTab {
  return (PROVIDER_TABS as readonly string[]).includes(value ?? '')
    ? (value as ProviderTab)
    : DEFAULT_TAB
}

// The Profile tab never edits the licenses, but a license cannot exist without a Professional
// Role: the shared rules run against the stored licenses, so the schema is built per fetched
// value, and clearing the role of a licensed provider is refused under the select rather than
// by the middleware's bare "Input validation failed".
const profileFormSchemaFor = (licenses: PractitionerDetail['licenses']) =>
  z
    .object({
      // The Professional Role and Specialty, `null` when none; the row's selects keep the
      // Specialty valid for the role.
      professionalRole: professionalRoleSchema.nullable(),
      specialty: specialtySchema.nullable(),
      npiNumber: npiNumberSchema,
      // The Languages in the order picked; empty means not recorded.
      languages: languagesSchema,
      // The Active Provider status (see the glossary); nothing reads it yet.
      activeProvider: z.boolean(),
      // The stored photo, `null` once removed. A picked one rides as `pendingPhoto` until Save
      // turns it into a Binary.
      photo: practitionerPhotoSchema.nullish(),
      pendingPhoto: z.instanceof(File).nullish(),
      // The Professional Liability Coverage as its card edits it: every field blank when none is
      // stored. A picked Supporting Document rides as `pendingFile` until Save turns it into a Binary.
      liability: liabilityCoverageSchema.extend({ pendingFile: z.instanceof(File).nullish() }),
    })
    .superRefine((data, ctx) => {
      for (const issue of [
        ...collectCredentialIssues({ ...data, licenses }),
        ...collectLiabilityIssues(data),
      ]) {
        ctx.addIssue({ code: 'custom', ...issue })
      }
    })
export type ProfileFormValues = z.infer<ReturnType<typeof profileFormSchemaFor>>

// The Licenses tab never edits the Professional Role, but a license cannot exist without one:
// the shared rules run against the stored role, so the schema is built per fetched value.
const licensesFormSchemaFor = (professionalRole: PractitionerDetail['professionalRole']) =>
  z
    .object({
      // A picked Supporting Document rides on its row until Save turns it into a Binary.
      licenses: z.array(licenseRowSchema.extend({ pendingFile: z.instanceof(File).nullish() })),
    })
    .superRefine((data, ctx) => {
      for (const issue of collectCredentialIssues({ ...data, professionalRole })) {
        ctx.addIssue({ code: 'custom', ...issue })
      }
    })
type LicensesFormValues = z.infer<ReturnType<typeof licensesFormSchemaFor>>
// A stable "none" so the Profile resolver is not rebuilt on every render before the fetch lands.
const EMPTY_LICENSES: PractitionerDetail['licenses'] = []

// The CAQH Profile as its tab edits it: every field blank when none is stored. The shared
// date rule runs here too, so the refusal shows under the due date before the request leaves.
const caqhFormSchema = z.object({ caqh: caqhProfileSchema }).superRefine((data, ctx) => {
  for (const issue of collectCaqhIssues(data)) {
    ctx.addIssue({ code: 'custom', ...issue })
  }
})
export type CaqhFormValues = z.infer<typeof caqhFormSchema>

// The Payer Enrollments as their tab edits them: the contract rows, a picked Supporting
// Document riding on its row until Save turns it into a Binary. The shared cross-row rules
// run here too, so a duplicate or an End date before the Effective date shows on its card
// before the request leaves. What Save receives is the parsed list (`PayerEnrollmentRowInput`):
// the resolver is stricter than the editable row (a fresh card's empty selects), hence the cast.
const payerEnrollmentsFormSchema = z
  .object({
    payerEnrollments: z.array(
      payerEnrollmentRowInputSchema.extend({ pendingFile: z.instanceof(File).nullish() }),
    ),
  })
  .superRefine((data, ctx) => {
    for (const issue of collectPayerEnrollmentIssues(data)) {
      ctx.addIssue({ code: 'custom', ...issue })
    }
  })
type PayerEnrollmentsSavedValues = z.infer<typeof payerEnrollmentsFormSchema>
const payerEnrollmentsResolver = zodResolver(payerEnrollmentsFormSchema) as unknown as Resolver<
  PayerEnrollmentsFormValues,
  unknown,
  PayerEnrollmentsSavedValues
>

const integrationsFormSchema = z.object({
  // Blank means "not mapped": the field is both the grant and the revoke.
  doseSpotClinicianId: doseSpotClinicianIdFieldSchema,
  // Blank means "not linked": the field is both the link and the unlink.
  bridgeProviderId: bridgeProviderIdFieldSchema,
  // Same rule for the Stedi provider-record mapping.
  stediProviderId: stediProviderIdFieldSchema,
})
type IntegrationsFormValues = z.infer<typeof integrationsFormSchema>

const toProfileValues = (p: PractitionerDetail): ProfileFormValues => ({
  professionalRole: p.professionalRole,
  specialty: p.specialty,
  npiNumber: p.npiNumber ?? '',
  languages: p.languages,
  activeProvider: p.activeProvider,
  photo: p.photo,
  pendingPhoto: null,
  liability: {
    policyName: p.liability?.policyName ?? '',
    coverageStart: p.liability?.coverageStart ?? null,
    coverageEnd: p.liability?.coverageEnd ?? null,
    document: p.liability?.document,
    pendingFile: null,
  },
})
const toLicensesValues = (p: PractitionerDetail): LicensesFormValues => ({
  licenses: p.licenses,
})
const toCaqhValues = (p: PractitionerDetail): CaqhFormValues => ({
  caqh: {
    providerId: p.caqh?.providerId ?? '',
    status: p.caqh?.status ?? null,
    lastAttestation: p.caqh?.lastAttestation ?? null,
    reattestationDue: p.caqh?.reattestationDue ?? null,
    notes: p.caqh?.notes ?? '',
  },
})
const toPayerEnrollmentsValues = (p: PractitionerDetail): PayerEnrollmentsFormValues => ({
  payerEnrollments: p.payerEnrollments,
})
const toIntegrationsValues = (p: PractitionerDetail): IntegrationsFormValues => ({
  doseSpotClinicianId: p.doseSpotClinicianId ?? '',
  bridgeProviderId: p.bridgeProviderId ?? '',
  stediProviderId: p.stediProviderId ?? '',
})

// The writes a tab's Save can run. Each mutation hook raises its own toast; the step names
// the inline alert so the user knows which write failed.
type SaveStep = 'document' | 'credentials' | 'dosespot' | 'bridge' | 'stedi'
const SAVE_STEP_FAILED: Record<SaveStep, string> = {
  document: 'Document not uploaded',
  credentials: 'Credentials not saved',
  dosespot: 'DoseSpot mapping not saved',
  bridge: 'Bridge mapping not saved',
  stedi: 'Stedi mapping not saved',
}

type Refetch = () => Promise<{ data: PractitionerDetail | undefined }>

async function uploadPendingDocuments<T extends { pendingFile?: File | null }>(
  medplum: MedplumClient,
  rows: T[],
): Promise<Omit<T, 'pendingFile'>[]> {
  return Promise.all(
    rows.map(async ({ pendingFile, ...row }) =>
      pendingFile ? { ...row, document: await uploadPendingFile(medplum, pendingFile) } : row,
    ),
  )
}

/**
 * One tab's form lifecycle: populated once the read lands and re-populated after a refetch
 * only while it holds no edits — react-query refetches in the background (window focus,
 * remount), and a reset then would silently drop what the user is typing. Keyed on
 * `dataUpdatedAt`, not on the data: react-query hands back the same object when a refetch
 * returns unchanged data. After a Save the form is reset explicitly from the refetched data,
 * whether or not the writes were accepted, so it shows what actually stuck — unless the tab
 * asks to keep its edits on failure (Profile and Licenses: a picked photo or Supporting
 * Document must survive a refused save so the retry needs no second pick).
 */
function useTabForm<T extends FieldValues>(
  form: UseFormReturn<T>,
  practitioner: PractitionerDetail | undefined,
  dataUpdatedAt: number,
  toValues: (p: PractitionerDetail) => T,
  refetch: Refetch,
) {
  const [saveError, setSaveError] = useState<{ title: string; message: string } | null>(null)
  // Spans the writes and the refetch that follows them, not just the mutations' isPending.
  const [isSaving, setIsSaving] = useState(false)

  const { isDirty } = form.formState
  useEffect(() => {
    if (practitioner && !isDirty) form.reset(toValues(practitioner))
  }, [practitioner, dataUpdatedAt, form, isDirty, toValues])

  // `write` runs the tab's mutations in order, starting at `firstStep`, and reports each
  // step it moves on to, so a failure midway is named while the earlier writes stay applied.
  const save = async (
    firstStep: SaveStep,
    write: (setStep: (step: SaveStep) => void) => Promise<void>,
    { keepEditsOnFailure = false } = {},
  ) => {
    setSaveError(null)
    setIsSaving(true)
    let step = firstStep
    let saved = true
    try {
      await write((next) => (step = next))
    } catch (error) {
      saved = false
      setSaveError({
        title: SAVE_STEP_FAILED[step],
        message: error instanceof Error ? error.message : 'Unknown error',
      })
    } finally {
      const { data } = await refetch()
      if (data && (saved || !keepEditsOnFailure)) form.reset(toValues(data))
      setIsSaving(false)
    }
  }

  return { isDirty, isSaving, saveError, save }
}

/**
 * Provider Operations detail: one Org Member's Credentials and integrations. A read-only
 * header (name, role, Organization, email), then five tabs, each its own form with its own
 * Save: Profile (photo, Professional Role and Specialty, NPI, Languages, Active Provider status
 * and Professional Liability Coverage), Licenses (the per-state licenses), CAQH (the CAQH
 * Profile), Payer Enrollments (one card per Payer Enrollment) and Integrations (the DoseSpot
 * clinician id, the Bridge provider and the Stedi provider). A Care Coordinator's page has no
 * tab strip and shows only the DoseSpot clinician
 * id. An inactive provider carries an "Inactive provider" badge in the header; nothing reads
 * the status yet.
 *
 * Profile, Licenses, CAQH and Payer Enrollments share the credentials writer, which
 * full-replaces role, specialty, NPI, languages, status, licenses, photo and coverage, so each
 * sends its own fields with the sibling's as last fetched; the CAQH Profile and the Payer
 * Enrollments are each sent by their own tab only. Integrations' Save
 * runs up to three sequential mutations, DoseSpot → Bridge → Stedi, each only when its value
 * changed: they are live vendor round-trips that an unrelated edit must not depend on. A
 * failure midway leaves the earlier writes applied; the tab names the failed step and
 * refetches, so the form shows what actually stuck.
 * Save is disabled while a tab is clean; a dirty tab carries a dot after its label. Switching
 * tabs keeps every tab's edits.
 *
 * The Bridge provider is visible to everyone and editable only by a super admin; the
 * middleware refuses a coordinator's write regardless. Its provider list is a live call
 * into Bridge and is requested only for a super admin. The Stedi provider is maintained by
 * a Care Coordinator like the DoseSpot mapping (PD-1961); the middleware's Provider
 * Operations access check gates both its list and its writer.
 */
export function ProviderPage() {
  const { practitionerId } = useParams<{ practitionerId: string }>()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const medplum = useMedplum()
  const isSuperAdmin = medplum.isSuperAdmin()
  const orpc = useOrpc()
  // The Organization the list (or the Super Admin members table) opened this page from. A
  // member with roles in two Organizations is otherwise ambiguous for a super admin.
  const requestedOrganizationId = searchParams.get(ORGANIZATION_QUERY_PARAM) ?? undefined
  const activeTab = parseTab(searchParams.get(TAB_QUERY_PARAM))
  const {
    data: practitioner,
    dataUpdatedAt,
    isLoading,
    isError,
    refetch,
  } = usePractitioner(practitionerId, requestedOrganizationId)
  const update = useUpdatePractitionerCredentials()
  const setDoseSpotClinician = useSetPractitionerDoseSpotClinician()
  const setBridgeProvider = useSetPractitionerBridgeProvider()
  const setStediProvider = useSetPractitionerStediProvider()

  const organizationId = practitioner?.organizationId
  // A Care Coordinator does not see patients or bill, so NPI, licenses and Bridge mean
  // nothing for them; only the DoseSpot mapping applies (a coordinator can be a DoseSpot
  // proxy or prescribing agent). The hidden fields keep their stored values, so Save never
  // writes them.
  const isCareCoordinator = practitioner?.roles.some(
    (role) => role.code === SNOMED_USER_ROLES.HOSPITAL_MANAGER.code,
  )
  const { data: bridge } = useQuery(
    orpc.listOrganizationBridgeProviders.queryOptions({
      input: { organizationId: organizationId ?? '' },
      enabled: isSuperAdmin && !isCareCoordinator && Boolean(organizationId),
    }),
  )

  // Stedi's provider list comes from the organization's Stedi account, so the picker and
  // the middleware validator agree. In test mode Stedi refuses the read wholesale
  // (testModeRestricted), so the picker degrades to manual entry of the record id.
  // Unlike Bridge, a Care Coordinator maintains this mapping (PD-1961); the middleware's
  // Provider Operations access check decides who may reach it, not the page. A coordinator
  // target gets no Stedi field at all, like Bridge.
  const { data: stedi } = useQuery(
    orpc.listOrganizationStediProviders.queryOptions({
      input: { organizationId: organizationId ?? '' },
      enabled: !isCareCoordinator && Boolean(organizationId),
    }),
  )

  // Name first, because that is what a super admin recognises; the NPI and credential
  // type disambiguate the namesakes. A mapping already saved is kept selectable even when
  // it is absent from the list — a rotated key or a provider retired on Bridge's side
  // would otherwise silently blank the field and read as "never linked". For a
  // coordinator the list is never requested, so the saved id is the only option.
  const bridgeOptions = useMemo(() => {
    const options = (bridge?.providers ?? []).map((p) => ({
      value: p.id,
      label: `${p.name} · NPI ${p.npi}${p.type ? ` · ${p.type}` : ''}`,
    }))
    const saved = practitioner?.bridgeProviderId
    if (saved && !options.some((o) => o.value === saved)) {
      options.unshift({ value: saved, label: bridge ? `${saved} (not in Bridge's list)` : saved })
    }
    return options
  }, [bridge, practitioner?.bridgeProviderId])

  const stediOptions = useMemo(() => {
    const options = (stedi?.providers ?? []).map((p) => ({
      value: p.id,
      label: `${p.name} · NPI ${p.npi}`,
    }))
    const saved = practitioner?.stediProviderId
    if (saved && !options.some((o) => o.value === saved)) {
      options.unshift({ value: saved, label: stedi ? `${saved} (not in Stedi's list)` : saved })
    }
    return options
  }, [stedi, practitioner?.stediProviderId])

  // react-hook-form reads the resolver off the latest render, so the rules follow a refetch.
  const storedLicenses = practitioner?.licenses ?? EMPTY_LICENSES
  const profileResolver = useMemo(
    () => zodResolver(profileFormSchemaFor(storedLicenses)),
    [storedLicenses],
  )
  const profileForm = useForm<ProfileFormValues>({
    resolver: profileResolver,
    defaultValues: {
      professionalRole: null,
      specialty: null,
      npiNumber: '',
      languages: [],
      activeProvider: true,
      photo: null,
      pendingPhoto: null,
      liability: { policyName: '', coverageStart: null, coverageEnd: null },
    },
  })
  const [photo, pendingPhoto] = useWatch({
    control: profileForm.control,
    name: ['photo', 'pendingPhoto'],
  })
  const storedRole = practitioner?.professionalRole ?? null
  const licensesResolver = useMemo(
    () => zodResolver(licensesFormSchemaFor(storedRole)),
    [storedRole],
  )
  const licensesForm = useForm<LicensesFormValues>({
    resolver: licensesResolver,
    defaultValues: { licenses: [] },
  })
  // The rule's issue lands on `professionalRole`, which this form holds no field for: the
  // hint below the tab's cards turns into the refusal.
  const licensesRefused = 'professionalRole' in licensesForm.formState.errors
  const caqhForm = useForm<CaqhFormValues>({
    resolver: zodResolver(caqhFormSchema),
    defaultValues: {
      caqh: {
        providerId: '',
        status: null,
        lastAttestation: null,
        reattestationDue: null,
        notes: '',
      },
    },
  })
  const payerEnrollmentsForm = useForm<
    PayerEnrollmentsFormValues,
    unknown,
    PayerEnrollmentsSavedValues
  >({
    resolver: payerEnrollmentsResolver,
    defaultValues: { payerEnrollments: [] },
  })
  const integrationsForm = useForm<IntegrationsFormValues>({
    resolver: zodResolver(integrationsFormSchema),
    defaultValues: { doseSpotClinicianId: '', bridgeProviderId: '', stediProviderId: '' },
  })
  const profile = useTabForm(profileForm, practitioner, dataUpdatedAt, toProfileValues, refetch)
  const licenses = useTabForm(licensesForm, practitioner, dataUpdatedAt, toLicensesValues, refetch)
  const caqh = useTabForm(caqhForm, practitioner, dataUpdatedAt, toCaqhValues, refetch)
  const payerEnrollments = useTabForm(
    payerEnrollmentsForm,
    practitioner,
    dataUpdatedAt,
    toPayerEnrollmentsValues,
    refetch,
  )
  const integrations = useTabForm(
    integrationsForm,
    practitioner,
    dataUpdatedAt,
    toIntegrationsValues,
    refetch,
  )

  // The Payer Enrollments tab alone asks the browser to confirm a reload while dirty.
  useWarnBeforeUnload(payerEnrollments.isDirty)

  // One save at a time across the tabs (see `SaveFooter`'s `held`).
  const anySaving =
    profile.isSaving ||
    licenses.isSaving ||
    caqh.isSaving ||
    payerEnrollments.isSaving ||
    integrations.isSaving

  const selectTab = (value: string | null) => {
    if (!value) return
    setSearchParams(
      (previous) => {
        previous.set(TAB_QUERY_PARAM, value)
        return previous
      },
      { replace: true },
    )
  }

  // Profile and Licenses each send the sibling as last fetched: the writer full-replaces
  // both, and a sibling someone else changed in between is re-applied stale, as the
  // single-form page did before. `edited` may run a step of its own (a file upload) before
  // the credentials call. The CAQH Profile and the Payer Enrollments ride only on their own
  // tab's Save: absent, the writer leaves them alone.
  const saveCredentials = (
    tab: typeof profile | typeof licenses | typeof caqh | typeof payerEnrollments,
    firstStep: SaveStep,
    edited: () => Promise<
      Partial<{
        professionalRole: PractitionerDetail['professionalRole']
        specialty: PractitionerDetail['specialty']
        npiNumber: string
        languages: PractitionerDetail['languages']
        activeProvider: boolean
        licenses: LicenseRow[]
        photo: PractitionerPhoto | null
        liability: LiabilityCoverage | null
        caqh: CaqhProfile | null
        payerEnrollments: PayerEnrollmentRowInput[]
      }>
    >,
    options?: { keepEditsOnFailure: boolean },
  ) => {
    if (!practitionerId || !practitioner) return
    return tab.save(
      firstStep,
      async (setStep) => {
        const fields = await edited()
        setStep('credentials')
        await update.mutateAsync({
          practitionerId,
          organizationId,
          professionalRole: practitioner.professionalRole,
          specialty: practitioner.specialty,
          npiNumber: practitioner.npiNumber ?? '',
          languages: practitioner.languages,
          activeProvider: practitioner.activeProvider,
          licenses: practitioner.licenses,
          photo: practitioner.photo,
          liability: practitioner.liability,
          ...fields,
        })
      },
      options,
    )
  }
  // A failed save keeps the tab's edits, picked files included, so the retry needs no second
  // pick; the Binary the failed attempt created is left behind (see the spec).
  const saveProfile = (values: ProfileFormValues) => {
    const { pendingFile: pendingDocument, ...coverage } = values.liability
    return saveCredentials(
      profile,
      values.pendingPhoto || pendingDocument ? 'document' : 'credentials',
      async () => {
        // The parse keeps the photo's three fields; the picker already checked its type.
        const photo = values.pendingPhoto
          ? practitionerPhotoSchema.parse(await uploadPendingFile(medplum, values.pendingPhoto))
          : values.photo
        const liability = pendingDocument
          ? { ...coverage, document: await uploadPendingFile(medplum, pendingDocument) }
          : coverage
        return {
          professionalRole: values.professionalRole,
          specialty: values.specialty,
          npiNumber: values.npiNumber,
          languages: values.languages,
          activeProvider: values.activeProvider,
          photo,
          // A card with every field blank and no document records nothing: the coverage is cleared.
          liability: Object.values(liability).some(Boolean) ? liability : null,
        }
      },
      { keepEditsOnFailure: true },
    )
  }
  const saveLicenses = (values: LicensesFormValues) =>
    saveCredentials(
      licenses,
      values.licenses.some((row) => row.pendingFile) ? 'document' : 'credentials',
      async () => ({ licenses: await uploadPendingDocuments(medplum, values.licenses) }),
      { keepEditsOnFailure: true },
    )

  const saveCaqh = (values: CaqhFormValues) =>
    saveCredentials(
      caqh,
      'credentials',
      () =>
        Promise.resolve({
          // A tab with every field blank records nothing: the profile is cleared.
          caqh: Object.values(values.caqh).some(Boolean) ? values.caqh : null,
        }),
      { keepEditsOnFailure: true },
    )

  // The whole list replaces what is stored; an empty list clears it.
  const savePayerEnrollments = (values: PayerEnrollmentsSavedValues) =>
    saveCredentials(
      payerEnrollments,
      values.payerEnrollments.some((row) => row.pendingFile) ? 'document' : 'credentials',
      async () => ({
        payerEnrollments: await uploadPendingDocuments(medplum, values.payerEnrollments),
      }),
      { keepEditsOnFailure: true },
    )

  const saveIntegrations = (values: IntegrationsFormValues) => {
    if (!practitionerId || !practitioner) return
    const stored = toIntegrationsValues(practitioner)
    const nextClinicianId = values.doseSpotClinicianId.trim() || null
    const doseSpotChanged = nextClinicianId !== (stored.doseSpotClinicianId || null)
    const nextBridgeProviderId = values.bridgeProviderId.trim() || null
    const bridgeChanged = nextBridgeProviderId !== (stored.bridgeProviderId || null)
    const nextStediProviderId = values.stediProviderId.trim() || null
    const stediChanged = nextStediProviderId !== (stored.stediProviderId || null)

    return integrations.save('dosespot', async (setStep) => {
      if (doseSpotChanged) {
        await setDoseSpotClinician.mutateAsync({
          practitionerId,
          organizationId,
          clinicianId: nextClinicianId,
        })
      }
      if (bridgeChanged) {
        setStep('bridge')
        await setBridgeProvider.mutateAsync({
          practitionerId,
          organizationId,
          bridgeProviderId: nextBridgeProviderId,
        })
      }
      if (stediChanged && organizationId) {
        setStep('stedi')
        await setStediProvider.mutateAsync({
          practitionerId,
          organizationId,
          stediProviderId: nextStediProviderId,
        })
      }
    })
  }

  // The list ignores the tab, so the back link carries only the Organization.
  const backSearch = new URLSearchParams(location.search)
  backSearch.delete(TAB_QUERY_PARAM)
  const backTo = { pathname: '/providers', search: backSearch.toString() }

  // Locked for the whole save and refetch: the form is reset from the refetch afterwards,
  // so anything typed meanwhile would be dropped.
  const profilePanel = (
    <form onSubmit={profileForm.handleSubmit(saveProfile)} noValidate>
      <fieldset disabled={profile.isSaving} className="m-0 min-w-0 border-0 p-0">
        <Stack>
          <PhotoField
            name={practitioner?.displayName ?? ''}
            photo={photo}
            pendingFile={pendingPhoto}
            onPick={(file) => profileForm.setValue('pendingPhoto', file, { shouldDirty: true })}
            onRemove={() => {
              profileForm.setValue('pendingPhoto', null, { shouldDirty: true })
              profileForm.setValue('photo', null, { shouldDirty: true })
            }}
          />
          <ProfessionalRoleFields control={profileForm.control} setValue={profileForm.setValue} />
          <Controller
            name="npiNumber"
            control={profileForm.control}
            render={({ field, fieldState }) => (
              <TextInput
                {...field}
                label="NPI Number"
                placeholder="Enter NPI number"
                error={fieldState.error?.message}
              />
            )}
          />
          <Controller
            name="languages"
            control={profileForm.control}
            render={({ field, fieldState }) => (
              <MultiSelect
                {...field}
                label="Languages"
                // Mantine keeps the placeholder beside the picked pills; shown only while none is.
                placeholder={field.value.length === 0 ? 'Select languages' : undefined}
                data={[...PRACTITIONER_LANGUAGES]}
                searchable
                error={fieldState.error?.message}
              />
            )}
          />
          <Controller
            name="activeProvider"
            control={profileForm.control}
            render={({ field }) => (
              <Switch
                name={field.name}
                ref={field.ref}
                checked={field.value}
                onChange={(event) => field.onChange(event.currentTarget.checked)}
                onBlur={field.onBlur}
                label="Active provider"
                description="Inactive providers will be excluded from patient allocation once it ships."
              />
            )}
          />
          <LiabilityCoverageField control={profileForm.control} setValue={profileForm.setValue} />
        </Stack>
      </fieldset>
      <SaveFooter {...profile} held={anySaving} />
    </form>
  )

  const licensesPanel = (
    <form onSubmit={licensesForm.handleSubmit(saveLicenses)} noValidate>
      <fieldset disabled={licenses.isSaving} className="m-0 min-w-0 border-0 p-0">
        <Stack>
          {/* A license cannot exist without a Professional Role, set on the Profile tab: a
              hint while none is stored, the refusal once a Save is tried regardless. */}
          {!storedRole && (
            <Alert
              color={licensesRefused ? 'red' : 'yellow'}
              variant="light"
              icon={<IconAlertCircle size={16} />}
              title={licensesRefused ? SAVE_STEP_FAILED.credentials : undefined}
            >
              {LICENSES_NEED_PROFESSIONAL_ROLE_MESSAGE}
            </Alert>
          )}
          <LicensesField control={licensesForm.control} setValue={licensesForm.setValue} />
        </Stack>
      </fieldset>
      <SaveFooter {...licenses} held={anySaving} />
    </form>
  )

  // The Billing Organization dropdown offers the caller's Provider Operations scope, the same
  // read the route guard already made.
  const { organizations: scopeOrganizations } = usePractitionerOpsScope()
  // The Payer choices come from the shared default payer catalog (the public one, as the
  // per-Organization list refuses a caller with roles in several Organizations). Until it
  // loads, or if it fails, the Payer has nothing to pick and a stored row shows blank there.
  const middlewareClient = useMiddlewareClient()
  const { data: catalogPayers } = useQuery({
    queryKey: ['insurance-payers-catalog'],
    queryFn: () => getInsurancePayersCatalog(middlewareClient),
  })
  const payerOptions = useMemo(() => toPayerOptions(catalogPayers ?? []), [catalogPayers])
  const payerEnrollmentsPanel = (
    <form onSubmit={payerEnrollmentsForm.handleSubmit(savePayerEnrollments)} noValidate>
      <fieldset disabled={payerEnrollments.isSaving} className="m-0 min-w-0 border-0 p-0">
        <PayerEnrollmentsField
          control={payerEnrollmentsForm.control}
          setValue={payerEnrollmentsForm.setValue}
          organizations={scopeOrganizations}
          payerOptions={payerOptions}
        />
      </fieldset>
      <SaveFooter
        {...payerEnrollments}
        held={anySaving}
        // Back to the list as last fetched; a failed save's kept edits go too. Asks first
        // (PD-2019): Cancel is only enabled while the tab is dirty, so every click would
        // drop real edits with no undo.
        onCancel={() =>
          modals.openConfirmModal({
            title: 'Discard changes?',
            children: <Text size="sm">Your unsaved Payer Enrollment edits will be lost.</Text>,
            labels: { confirm: 'Discard changes', cancel: 'Keep editing' },
            confirmProps: { color: 'red' },
            onConfirm: () =>
              practitioner && payerEnrollmentsForm.reset(toPayerEnrollmentsValues(practitioner)),
          })
        }
      />
    </form>
  )

  const caqhPanel = (
    <form onSubmit={caqhForm.handleSubmit(saveCaqh)} noValidate>
      <fieldset disabled={caqh.isSaving} className="m-0 min-w-0 border-0 p-0">
        <CaqhProfileField control={caqhForm.control} />
      </fieldset>
      <SaveFooter {...caqh} held={anySaving} />
    </form>
  )

  const integrationsPanel = (
    <form onSubmit={integrationsForm.handleSubmit(saveIntegrations)} noValidate>
      <fieldset disabled={integrations.isSaving} className="m-0 min-w-0 border-0 p-0">
        <Stack>
          <Controller
            name="doseSpotClinicianId"
            control={integrationsForm.control}
            render={({ field, fieldState }) => (
              <TextInput
                {...field}
                label="DoseSpot clinician id"
                placeholder="Numeric DoseSpot clinician id"
                description="Grants ePrescribing. The id is checked against DoseSpot, which decides whether it is a prescriber, proxy or prescribing agent; a prescriber's id must belong to this member's NPI. Clearing it revokes prescribing immediately."
                error={fieldState.error?.message}
              />
            )}
          />

          {!isCareCoordinator && (
            <>
              <Divider />

              <Controller
                name="bridgeProviderId"
                control={integrationsForm.control}
                render={({ field, fieldState }) => (
                  <Select
                    {...field}
                    clearable
                    searchable
                    label="Bridge provider"
                    clearButtonProps={{ 'aria-label': 'Clear Bridge provider' }}
                    placeholder={
                      bridge && !bridge.configured
                        ? "Save the organization's Bridge API key first"
                        : 'Not linked'
                    }
                    disabled={!isSuperAdmin || (Boolean(bridge) && !bridge?.configured)}
                    data={bridgeOptions}
                    description={
                      isSuperAdmin
                        ? "Links this member to their Bridge provider record, so Bridge's eligibility answers name them. The provider is checked against Bridge and must hold this member's NPI."
                        : 'Links this member to their Bridge provider record. Only a super admin can change it.'
                    }
                    error={fieldState.error?.message}
                    value={field.value || null}
                    onChange={(value) => field.onChange(value ?? '')}
                  />
                )}
              />

              <Divider />

              {/* In test mode Stedi refuses provider reads, so the picker becomes a
                      text field for the record id; the middleware stores it unverified. */}
              <Controller
                name="stediProviderId"
                control={integrationsForm.control}
                render={({ field, fieldState }) =>
                  stedi?.testModeRestricted ? (
                    <TextInput
                      {...field}
                      label="Stedi provider"
                      placeholder="Stedi provider record id (UUID)"
                      description="This organization runs Stedi in test mode, where Stedi refuses provider reads — paste the provider record id from Stedi's portal. It is saved unverified and checked for real on a live key."
                      error={fieldState.error?.message}
                    />
                  ) : (
                    <Select
                      {...field}
                      clearable
                      searchable
                      label="Stedi provider"
                      clearButtonProps={{ 'aria-label': 'Clear Stedi provider' }}
                      placeholder={
                        stedi && !stedi.configured
                          ? "Save the organization's Stedi API key first"
                          : 'Not linked'
                      }
                      disabled={Boolean(stedi) && !stedi?.configured}
                      data={stediOptions}
                      description="Links this member to their Stedi provider record, so Stedi's enrollments and remittances name them. The record is checked against Stedi and must hold this member's NPI."
                      error={fieldState.error?.message}
                      value={field.value || null}
                      onChange={(value) => field.onChange(value ?? '')}
                    />
                  )
                }
              />
            </>
          )}
        </Stack>
      </fieldset>
      <SaveFooter {...integrations} held={anySaving} />
    </form>
  )

  return (
    <div className="box-border min-h-[calc(100vh-64px)] bg-[#F6F7F9] p-4 text-[#1F2733]">
      <div className="mx-auto flex max-w-[880px] flex-col gap-4 rounded-[18px] border border-[#E7E9EC] bg-white px-6 pt-6 pb-6 shadow-[0_1px_3px_rgba(16,24,40,.05),0_12px_30px_rgba(16,24,40,.05)]">
        <Anchor
          component={Link}
          to={backTo}
          size="sm"
          c="dimmed"
          className="inline-flex items-center gap-1"
        >
          <IconArrowLeft size={16} />
          Back to Provider Operations
        </Anchor>

        {isLoading && <Loader />}

        {isError && (
          <Alert
            color="red"
            icon={<IconAlertCircle size={16} />}
            title="Could not load this practitioner"
          >
            You may not have access to this practitioner, or the record no longer exists.
          </Alert>
        )}

        {practitioner && (
          <>
            <div className="flex flex-col gap-1">
              <div className="flex flex-wrap items-center gap-2.5">
                <h1 className="text-xl font-bold tracking-tight text-[#1F2733]">
                  {practitioner.displayName || 'Unnamed practitioner'}
                </h1>
                {isCareCoordinator ? (
                  <Badge size="sm" variant="outline">
                    Care Coordinator
                  </Badge>
                ) : (
                  <>
                    <Badge size="sm" color="gray" variant="light">
                      Practitioner
                    </Badge>
                    {!practitioner.activeProvider && (
                      <Badge size="sm" color="gray" variant="light">
                        Inactive provider
                      </Badge>
                    )}
                  </>
                )}
              </div>
              <Text size="sm" c="dimmed">
                {practitioner.organizationName}
              </Text>
              {practitioner.email && (
                <Text size="sm" c="dimmed">
                  {practitioner.email}
                </Text>
              )}
            </div>

            <Divider />

            {isCareCoordinator ? (
              integrationsPanel
            ) : (
              // Panels unmount when inactive; edits live in the forms above, so they survive.
              <Tabs value={activeTab} onChange={selectTab} keepMounted={false} color={ACCENT}>
                <Tabs.List>
                  <Tabs.Tab
                    value="profile"
                    rightSection={profile.isDirty ? <DirtyDot /> : undefined}
                  >
                    Profile
                  </Tabs.Tab>
                  <Tabs.Tab
                    value="licenses"
                    rightSection={licenses.isDirty ? <DirtyDot /> : undefined}
                  >
                    Licenses
                  </Tabs.Tab>
                  <Tabs.Tab value="caqh" rightSection={caqh.isDirty ? <DirtyDot /> : undefined}>
                    CAQH
                  </Tabs.Tab>
                  <Tabs.Tab
                    value="payer-enrollments"
                    rightSection={payerEnrollments.isDirty ? <DirtyDot /> : undefined}
                  >
                    Payer Enrollments
                  </Tabs.Tab>
                  <Tabs.Tab
                    value="integrations"
                    rightSection={integrations.isDirty ? <DirtyDot /> : undefined}
                  >
                    Integrations
                  </Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel value="profile" pt="md">
                  {profilePanel}
                </Tabs.Panel>
                <Tabs.Panel value="licenses" pt="md">
                  {licensesPanel}
                </Tabs.Panel>
                <Tabs.Panel value="caqh" pt="md">
                  {caqhPanel}
                </Tabs.Panel>
                <Tabs.Panel value="payer-enrollments" pt="md">
                  {payerEnrollmentsPanel}
                </Tabs.Panel>
                <Tabs.Panel value="integrations" pt="md">
                  {integrationsPanel}
                </Tabs.Panel>
              </Tabs>
            )}
          </>
        )}
      </div>
    </div>
  )
}
